import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { QuoteVersionExportPacket } from '../types/admin';

/**
 * Loads the export packet for a saved quote version. Prefetched as soon as a
 * version is selected so the copy buttons can write to the clipboard
 * synchronously inside the tap (Safari drops clipboard writes after an await).
 */
export function useVersionExportPacket(
  orgId: string | null,
  quoteId: string | undefined,
  versionId: string | null | undefined,
): { packet: QuoteVersionExportPacket | null; loading: boolean; error: string | null } {
  const [packet, setPacket] = useState<QuoteVersionExportPacket | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPacket(null);
    setError(null);
    if (!orgId || !quoteId || !versionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: QuoteVersionExportPacket }>(
          `/quotes/${orgId}/${quoteId}/versions/${versionId}/export`,
        );
        if (!cancelled) setPacket(res.data);
      } catch (err) {
        console.error('[versionExportPacket]', err);
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load export');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, quoteId, versionId]);

  return { packet, loading, error };
}
